/* ================================================================ */
// DATA ACTIONS | index.js
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import 'dotenv/config';
import PropTypes from 'prop-types';
import axios from 'axios';
import WebFont from 'webfontloader';
// Auth0
import { getTokenSilently } from '../../auth0/auth0';
// Constants
import { LoadingTypes, VisualisationTypes, PolygonLayer, LineLayer, PointLayer, ImageLayer } from '@myglobe/core/dist/types';
import { updateMapConfig } from '../Map';
/* ========================================== */
// Actions
/* ========================================== */
// ----- EXTERNAL STATE ACTIONS -----
// > Story Data
import * as Content from './Content';
export { Content };
// > Mapping Data
import * as Mapping from './Mapping';
export { Mapping };
// > Dataset Data
import * as Datasets from './Datasets';
export { Datasets };
// ----- FETCH DATASETS -----
// > First Fetch (Globe and All)
export const FETCH_INIT = 'FETCH_INIT';
export const FETCH_INIT_FAILURE = 'FETCH_INIT_FAILURE';
export const FETCH_INIT_SUCCESS = 'FETCH_INIT_SUCCESS';
// > Globe Config Data
export const FETCH_GLOBE = 'FETCH_GLOBE';
export const FETCH_GLOBE_FAILURE = 'FETCH_GLOBE_FAILURE';
export const FETCH_GLOBE_SUCCESS = 'FETCH_GLOBE_SUCCESS';
// ----- MUTATE DATASETS -----
export const ADD_DATA_ITEM = 'ADD_DATA_ITEM';
export const ADD_DATA_HASHMAP = 'ADD_DATA_HASHMAP';
export const ADD_DATA_ARRAY = 'ADD_DATA_ARRAY';
export const UPDATE_DATA_ITEM = 'UPDATE_DATA_ITEM';
export const REMOVE_DATA_ITEM = 'REMOVE_DATA_ITEM';
/* ========================================== */
// Data Indexes
/* ========================================== */
export const indexes = {
    layer: 'name',
    state: 'name',
    page: 'slug',
    story: 'slug',
    panel: 'slug',
    datasets: 'name'
};
/* ========================================== */
// Helper Functions
/* ========================================== */
/* ========================================== */
/**
 * Mappify
 * -------
 * Transforms node array into a map by
 * indexing with the given key index
 *
 * @param {string} keyIndex index id to use as map key
 * @param {array} array array to convert
 * @param {array} keyPrefixes additional keys to prefix key id with
 */
export function mappify(keyIndex, array, keyPrefixes) {
    console.debug('Data (Actions) | ', 'mappify -', 'keyIndex:', keyIndex, 'array:', array, 'keyPrefixes', keyPrefixes);
    return array.reduce((map, obj, index) => {
        // Format object layer names into simple array structure
        const key = keyPrefixes && keyPrefixes[index] ? `${keyPrefixes[index]}:${obj[keyIndex]}` : obj[keyIndex];
        map[key] = obj;
        return map;
    }, {});
}
/* ========================================== */
// API Functions
/* ========================================== */
//const { getTokenSilently } = useAuth0();
/*
<Auth0Context.Consumer>
    { props => /* custom components */ /*}
</Context.Consumer>*/
export const callAPI = async (query, variables) => {
    try {
        //var token = await this.getTokenSilently();
        const response = await fetch(process.env.LOCAL_API + '/graphql' || 'https://api.myglobe.app/graphql', {
            headers: {
                //Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            method: 'POST',
            body: JSON.stringify({ query: query, variables })
        });
        console.debug('Data (Actions) | ', 'callAPI - ', 'responseData:', responseData);
        const responseData = await response.json();
        return responseData;
    } catch (e) {
        console.error(e);
    }
};
export const callAPISecurely = async (query, variables) => {
    var token = await getTokenSilently();
    try {
        const response = await fetch(process.env.LOCAL_API + '/graphql' || 'https://api.myglobe.app/graphql', {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            method: 'POST',
            body: JSON.stringify({ query, variables })
        });
        console.debug('Data (Actions) | ', 'callAPISecurely - ', 'responseData:', responseData);
        const responseData = await response.json();
        return responseData;
    } catch (e) {
        console.error(e);
    }
};
// temp - move to postgraphile using custom schema plugins
export const callEditCSV = async (query) => {
    try {
        //var token = await this.getTokenSilently();
        const response = await fetch(process.env.LOCAL_API + '/editcsv' || 'https://api.myglobe.app/editcsv', {
            headers: {
                //Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            method: 'POST',
            body: JSON.stringify(query)
        });
        console.debug('Data (Actions) | ', 'callEditCSV - ', 'responseData:', responseData);
        const responseData = await response.json();
        return responseData;
    } catch (e) {
        console.error(e);
    }
};

export const callUploadMedia = async (file) => {
    console.log('calluploadmedia');

    //var token = await this.getTokenSilently();
    try {
        var data = new FormData();
        data.append('file', file);
        const response = await fetch(process.env.LOCAL_API + '/uploadmedia' || 'https://api.myglobe.app/uploadmedia', {
            headers: {
                //Authorization: `Bearer ${token}`,
            },
            method: 'POST',
            body: data
        });
        const responseData = await response.json();
        return { ...responseData, ...{ status: response.status } };
    } catch (err) {
        return err;
    }

};
/* ========================================== */
// DATASET Functions
/* ========================================== */
/* ========================================== */
/**
 * Add Data
 * --------
 * Inserts data in either a bulk array format or
 * single data item to a given dataset's state
 *
 * @param {string} datasetID dataset's state id
 * @param {array || object} incomingData array or data item
 */
export function addData(datasetID, incomingData) {
    return (dispatch) => {
        if (Array.isArray(incomingData)) {
            // Incoming Data = [{},{},...]
            dispatch({ type: ADD_DATA_ARRAY, datasetID, payload: incomingData });
        } else if (typeof incomingData === 'object' && Object.keys(incomingData).includes('id')) {
            // Incoming Data = {id: '', data:{}|[]}
            // NOTE: single insertions do not override existing data and will return the same state (use UPDATE_DATA_ITEM)
            dispatch({ type: ADD_DATA_ITEM, datasetID, itemID: incomingData.id, payload: incomingData });
        } else if (typeof incomingData === 'object') {
            // Incoming Data = {[id]:{},[id2]:{},...}
            // NOTE: bulk insertions override data that currently exists
            dispatch({ type: ADD_DATA_HASHMAP, datasetID, payload: incomingData });
        }
    };
}
addData.propTypes = {
    datasetID: PropTypes.string.isRequired,
    incomingData: PropTypes.oneOf([
        PropTypes.object,
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            data: PropTypes.oneOf([PropTypes.object, PropTypes.array]).isRequired
        }),
        PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                data: PropTypes.arrayOf(PropTypes.oneOf([PropTypes.object, PropTypes.array])).isRequired
            })
        )
    ]).isRequired
};
/* ========================================== */
// FETCH Functions
/* ========================================== */
/* ========================================== */
/**
 * Fetch Init
 * ----------
 * Fetches all globe data on first load
 *
 * @param {string} globeName globe name identifier
 */
export function fetchInit(globeName) {
    return (dispatch) => {
        dispatch({ type: FETCH_INIT });
        // First fetch for globe query
        const query = `{
            globes(condition: {name: "${globeName}"}, first: 1) {
                nodes {
                    id
                    defaultState
                    mapboxToken
                    lastUpdated
                    config
                    name
                    title
                    pages {
                      nodes {
                        uuid: id
                        name
                        slug
                        type
                        content
                        style
                      }
                    }
                    datasets {
                      nodes {
                        uuid: id
                        name
                        type
                        endpoint
                        geoId
                      }
                    }
                    stories {
                      nodes {
                        uuid: id
                        slug
                        title
                        storyPanels {
                          nodes {
                            panel {
                              slug
                              uuid: id
                              position
                              sections
                              state {
                                name
                              }
                              title
                              type
                              interactions
                              background
                            }
                          }
                        }
                      }
                    }
                    visStates {
                      nodes {
                        uuid: id
                        name
                        mapStyle
                        viewState
                        layers: stateLayersByStateId {
                          nodes {
                            layer {
                              name
                            }
                          }
                        }
                      }
                    }
                    visLayers {
                      nodes {
                        name
                        type
                        uuid: id
                        lastUpdated
                        dataset {
                          name
                        }
                      }
                    }
                    email
                    account: accountByAccount {
                      mapboxToken
                      mapboxId        
                    }
                }
            }
        }`;

        return callAPI(query)
            .then(response => {
                const { data } = response;
                const globeData = data.globes.nodes[0];

                // Get Globe Data
                const globePayload = {
                    uuid: globeData.id,
                    defaultState: globeData.defaultState,
                    mapboxToken: globeData.mapboxToken,
                    mapboxUserId: globeData.owner.mapboxUserId,
                    owner: globeData.owner.name,
                    config: globeData.config,
                    name: globeData.name,
                    title: globeData.title,
                    lastUpdated: globeData.lastUpdated
                };

                const testStateIds = globeData.states.nodes.map(stateNode => stateNode.uuid);
                dispatch(Mapping.fetchStates(testStateIds));

                dispatch(updateMapConfig(globePayload));
                dispatch({ type: FETCH_GLOBE_SUCCESS, payload: globePayload });

                // Get Globe Pages
                const pages = globeData.pages.nodes;
                const pagesPayload = mappify(indexes['page'], Object.values(pages));
                dispatch(addData('pages', pagesPayload));
                dispatch({ type: Content.FETCH_PAGES_SUCCESS });

                // Get Globe Datasets
                const getDatasets = Promise.all(globeData.datasets.nodes.map(async datasetNode => {
                    const { uuid, name, type, endpoint, geoId, lastUpdated } = datasetNode;
                    console.debug('Data (Actions) | ', 'fetchInit -', 'datasetNode:', datasetNode);
                    // Fetch Dataset Endpoint Data
                    const endpointData = await Datasets.fetchEndpointData(endpoint);
                    return { uuid, name, type, endpoint, geoId, lastUpdated, endpointData };
                }) || []);
                getDatasets.then(datasets => {
                    console.debug('Data (Actions) | ', 'fetchInit -', 'datasets:', datasets);
                    const datasetsPayload = mappify(indexes['datasets'], Object.values(datasets));
                    dispatch(addData('datasets', datasetsPayload));
                    dispatch({ type: Datasets.FETCH_DATASETS_SUCCESS });
                });

                // Get Globe Stories
                const stories = globeData.stories.nodes.map(storyNode => {
                    const { uuid, slug, title } = storyNode;
                    // Get array of panel slugs
                    const panels = storyNode.panels.nodes.map(panelNode => `${storyNode.slug}:${panelNode.panel.slug}`);
                    return { uuid, slug, title, panels };
                });
                const storiesPayload = mappify(indexes['story'], Object.values(stories));
                dispatch(addData('stories', storiesPayload));
                dispatch({ type: Content.FETCH_STORIES_SUCCESS, payload: storiesPayload });

                // Get Globe Panels
                let panelStorySlugs = [];
                const panels = globeData.stories.nodes.map(storyNode => storyNode.panels.nodes.map(panel => {
                    // Add Story Slug to panel sequence
                    panelStorySlugs.push(storyNode.slug);
                    return {
                        ...panel.panel,
                        state: panel.panel.state.name,
                        sections: panel.panel.sections
                    };
                })).flat();
                const panelsPayload = mappify(indexes['panel'], panels, Object.values(panelStorySlugs));
                dispatch(addData('panels', panelsPayload));
                dispatch({ type: Content.FETCH_PANELS_SUCCESS });

                // Get Globe States
                const states = globeData.states.nodes.map(stateNode => {
                    const { uuid, name, mapStyle } = stateNode;
                    // Get array of layer names
                    const layers = stateNode.layers.nodes.map(layer => {
                        return layer.layer.name;
                    });
                    return { uuid, name, mapStyle, layers };
                });
                const statesPayload = mappify(indexes['state'], Object.values(states));
                dispatch(addData('states', statesPayload));
                dispatch({ type: Mapping.FETCH_STATES_SUCCESS });

                // Get Globe Layers
                const layers = globeData.layers.nodes.map(layerNode => {
                    const { uuid, name, type, style, lastUpdated } = layerNode;
                    const dataset = layerNode.dataset ? layerNode.dataset.name : null;
                    return { uuid, name, type, style, lastUpdated, dataset };
                });
                const layersPayload = mappify(indexes['layer'], Object.values(layers));
                dispatch(addData('layers', layersPayload));
                dispatch({ type: Mapping.FETCH_LAYERS_SUCCESS });
            })
            .catch(error => {
                console.warn(error);
                dispatch({ type: FETCH_INIT_FAILURE, error });
            });
    };
}
/* ========================================== */
/**
 * Fetch Globe
 * -----------
 * Retrieves the globe instance config and default
 * state
 *
 * @param {string} globeName globe uri name (subdomain)
 */
export function fetchGlobe(globeName) {
    console.log(globeName);
    return (dispatch) => {
        dispatch({ type: FETCH_GLOBE });
        // Fetch Globe
        const query = `{
            globes(condition: {name: "${globeName}"}, first: 1) {
                nodes {
                    uuid: id
                    defaultState: visStates(condition: {name: "default"}, first: 1) {
                        nodes {
                            uuid: id
                            name
                        }
                    }
                    mapboxToken
                    lastUpdated
                    config
                    name
                    title
                    pages {
                        nodes {
                            uuid: id
                        }
                    }
                    stories {
                        nodes {
                            uuid: id
                        }
                    }
                }
            }
        }`;
        return callAPI(query)
            .then(response => {
                const { data } = response;
                const globeData = data.globes.nodes[0];
                const defaultStateUUID = globeData.defaultState.nodes.map(node => node.uuid);
                const pageUUIDs = globeData.pages.nodes.map(node => node.uuid);
                const storyUUIDs = globeData.stories.nodes.map(node => node.uuid);
                const payload = {
                    ...globeData,
                    defaultState: globeData.defaultState.nodes[0].name,
                    //config: JSON.parse(globeData.config)
                };
                if (payload.config.theme) {
                    // Get WebFonts
                    getFontsFromTheme(payload.config.theme);
                }
                dispatch(Mapping.fetchStates(defaultStateUUID));
                dispatch(Content.fetchPages(pageUUIDs));
                dispatch(Content.fetchStories(storyUUIDs));
                dispatch({ type: FETCH_GLOBE_SUCCESS, payload });
            })
            .catch(error => {
                console.warn(error);
                dispatch({ type: FETCH_GLOBE_FAILURE, error });
            });
    };
}
function getFontsFromTheme(theme) {
    const typography = theme.typography || {};
    let fontFamilies = {};
    let rootFontFamilies = typography.fontFamily?.match(/\w[\w\s-]+/g);
    let fontWeight = typography.fontWeight || 'normal';
    rootFontFamilies?.forEach(fontFamily => {
        if (Object.prototype.hasOwnProperty.call(fontFamilies, fontFamily)) {
            fontFamilies[fontFamily].add(fontWeight);
        } else {
            fontFamilies[fontFamily] = new Set().add(fontWeight);
        }
    });
    Object.keys(typography).forEach(typographyKey => {
        let typographyItem = typography[typographyKey];
        if (typeof typographyItem === 'object' && typography[typographyKey].fontFamily) {
            console.debug('Data (Actions) | ', 'getFontsFromTheme -', 'typographyKey:', typographyKey, 'fontFamily:', typography[typographyKey].fontFamily);
            let elementFontFamilies = typography[typographyKey].fontFamily.match(/\w[\w\s-]+/g);
            //let fontFamily = typography[typographyKey].fontFamily;
            let fontWeight = typography[typographyKey].fontWeight || 'normal';
            elementFontFamilies.forEach(fontFamily => {
                if (Object.prototype.hasOwnProperty.call(fontFamilies, fontFamily)) {
                    fontFamilies[fontFamily].add(fontWeight);
                } else {
                    fontFamilies[fontFamily] = new Set().add(fontWeight);
                }
            });
        }
    });
    const families = Object.keys(fontFamilies).map(fontFamily => {
        const fontWeights = Array.from(fontFamilies[fontFamily]).join(',');
        return `${fontFamily}:${fontWeights}`;
    });
    console.debug('Data (Actions) | ', 'getFontsFromTheme -', 'families:', families);
    WebFont.load({
        google: {
            families
        }
    });
}